/**
 * Interactive marquee driver.
 *
 * Continuous auto-scroll, arrow buttons and finger/mouse drag all move ONE
 * position value, so they can never fight each other the way a CSS animation
 * plus scrollBy() does. After any manual interaction the auto-scroll holds off
 * for `resumeDelay` ms instead of snapping straight back into motion.
 *
 * Expects a track whose children are the cards rendered twice (one real set
 * followed by an identical clone set), inside a viewport with overflow hidden.
 *
 * Usage:
 *   const m = window.dwfashionMarquee(trackEl, { duration: 30, reverse: false });
 *   m.nudge(240);   // arrow button: shift by one card, pausing auto-scroll
 *   m.destroy();
 */
window.dwfashionMarquee = function dwfashionMarquee(track, options) {
  if (!track || track.children.length < 2) return null;

  var opts = options || {};
  var duration = parseFloat(opts.duration) || 30; // seconds for one full set
  var reverse = !!opts.reverse;
  var pauseOnHover = opts.pauseOnHover !== false;
  var resumeDelay = opts.resumeDelay != null ? opts.resumeDelay : 2500;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var pos = 0;
  var target = 0;
  var hovered = false;
  var dragging = false;
  var pausedUntil = 0;
  var rafId = null;

  // The CSS keyframe animation (if any) must go, or it would move the same
  // element independently of the transform this driver writes.
  track.style.animation = 'none';
  track.style.willChange = 'transform';

  /**
   * Distance of exactly one set of cards, measured from where the first clone
   * starts. Deriving it from scrollWidth/2 is wrong: the track has N-1 gaps
   * across 2N cards, so half of it is short by half a gap and the loop visibly
   * jumps every cycle.
   */
  var setWidth = 0;
  function measure() {
    var kids = track.children;
    var half = Math.floor(kids.length / 2);
    if (half > 0 && kids[half]) {
      setWidth = kids[half].offsetLeft - kids[0].offsetLeft;
    }
    if (!setWidth || setWidth < 1) setWidth = track.scrollWidth / 2;
  }
  measure();
  window.addEventListener('resize', measure);

  function wrap() {
    if (setWidth > 0) {
      while (pos >= setWidth) {
        pos -= setWidth;
        target -= setWidth;
      }
      while (pos < 0) {
        pos += setWidth;
        target += setWidth;
      }
    }
    track.style.transform = 'translate3d(' + -pos + 'px, 0, 0)';
  }

  function hold() {
    pausedUntil = Date.now() + resumeDelay;
  }

  var last = null;
  function step(ts) {
    if (last === null) last = ts;
    var dt = Math.min((ts - last) / 1000, 0.1);
    last = ts;

    if (Math.abs(target - pos) > 0.5) {
      // Easing toward an arrow-driven target.
      pos += (target - pos) * 0.18;
      wrap();
    } else {
      pos = target;
      // No document.hidden guard: requestAnimationFrame already stops in a
      // background tab, and dt is clamped, so returning to the tab cannot jump.
      // Some embedded/in-app browsers report hidden incorrectly and that would
      // leave the marquee frozen for those shoppers.
      var idle = !hovered && !dragging && Date.now() >= pausedUntil;
      if (idle && !reduceMotion && setWidth > 0) {
        pos += (setWidth / duration) * dt * (reverse ? -1 : 1);
        target = pos;
        wrap();
      }
    }
    rafId = requestAnimationFrame(step);
  }
  rafId = requestAnimationFrame(step);

  if (pauseOnHover) {
    track.addEventListener('mouseenter', function () {
      hovered = true;
    });
    track.addEventListener('mouseleave', function () {
      hovered = false;
    });
  }

  // ---- drag ---------------------------------------------------------------
  var startX = 0;
  var startY = 0;
  var startPos = 0;
  var axis = null; // null = undecided, 'x' = we own it, 'y' = page scroll

  function dragStart(x, y, lockAxis) {
    dragging = true;
    startX = x;
    startY = y;
    startPos = pos;
    axis = lockAxis ? 'x' : null;
  }

  function dragMove(x, y) {
    if (!dragging) return;
    var dx = x - startX;
    var dy = y - startY;

    // On touch, wait until the gesture shows its intent. If it is mostly
    // vertical the shopper is scrolling the page, so let go of it entirely --
    // otherwise the carousel yanks sideways during a normal page scroll.
    if (axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (axis === 'y') {
        dragging = false;
        return;
      }
    }

    pos = startPos - dx;
    target = pos;
    wrap();
  }

  function dragEnd() {
    if (dragging || axis === 'x') hold();
    dragging = false;
    axis = null;
  }

  track.addEventListener(
    'touchstart',
    function (e) {
      if (e.touches && e.touches.length === 1) {
        dragStart(e.touches[0].clientX, e.touches[0].clientY, false);
      }
    },
    { passive: true }
  );

  track.addEventListener(
    'touchmove',
    function (e) {
      if (e.touches && e.touches.length === 1) {
        dragMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    { passive: true }
  );

  track.addEventListener('touchend', dragEnd, { passive: true });
  track.addEventListener('touchcancel', dragEnd, { passive: true });

  track.addEventListener('mousedown', function (e) {
    if (e.button === 0) {
      e.preventDefault();
      dragStart(e.clientX, e.clientY, true);
    }
  });

  function onMouseMove(e) {
    if (dragging) dragMove(e.clientX, e.clientY);
  }
  function onMouseUp() {
    dragEnd();
  }
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);

  return {
    /** Shift by `amount` px (negative = back) and hold the auto-scroll. */
    nudge: function (amount) {
      target += amount;
      hold();
    },
    measure: measure,
    destroy: function () {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    },
  };
};
